import styled from 'styled-components';
import React, { FC } from 'react';
import { TFunction, useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { filter, find, isEmpty, isNil, map, truncate } from 'lodash-es';
import {
    Alert,
    Banner,
    CollectSvg,
    ContentLoader,
    CustomTable,
    CustomTableHeadersProps,
    CustomTableRowsProps,
    formatNumber,
    MobileTable,
    palette,
    Paper,
    SimpleLoader,
    TemplatePreview,
    Typography,
} from '@dev/ee-components';
import {
    AdvancedOptions,
    Campaign,
    CampaignSimpleStats,
    CampaignTemplateStats,
    Sender,
} from '@dev/ee-api/ee-api-models';
import { Box, Link } from '@material-ui/core';
import { Dispatch } from 'redux';

import useMobile from '@/services/hooks/useMobile';
import { PreviewCampaign } from '@/store/campaigns/actions';
import { PickedCampaignInterface, WinnerResultsTableType } from '@/store/campaigns/types';
import { ApplicationState } from '@/store';
import { getApiPath } from '@/services/Secuirty';
import { links } from '@/services/dictionaries/links';
import { isReseller } from '@/services/Secuirty';
import { StatusBadge } from '@/assets/StatusBadge';

import { getIsWinner, getWinnerTemplate, isNewAbSplit } from '../../../helperFunctions';
import {
    isClickTrackingEnabled,
    isLinkClickTrackingEnabled,
    isOpenTrackingEnabled,
} from '../../Overview/helperFunctions';
import { DisabledTrackingAlert } from '../../components/DisabledTrackingAlert';

const TableSenderInfo = styled.div`
    color: #a0aec0;
    font-size: 11px;
    font-family: 'Mulish';
    font-weight: Regular;
`;

const TableItem = styled(TableSenderInfo)`
    text-align: right;
    margin-top: 2px;
`;

const TableItemBox = styled.div`
    text-align: right;
`;

const TableTitleWrapper = styled.div`
    display: flex;
    align-items: center;
`;

const SenderEmail = styled.div`
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    font-size: 11px;
`;

const BadgeWrapper = styled.div`
    margin-top: 8px;
`;

const SmallerNumber = styled.div`
    font-size: 11px;
    font-family: 'Mulish';
    color: ${palette.gray[5]};
`;

const SenderName = styled.div`
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
`;

export const GetWinnerResults: FC<WinnerResultsTableType> = (props: WinnerResultsTableType) => {
    const { pickedCampaign, advancedOptions } = props;
    const { simpleStats } = pickedCampaign;
    const { t } = useTranslation();
    const isMobile = useMobile();
    const dispatch = useDispatch();

    return (
        <Box mt="64px">
            <Typography variant="h1">{t('winnerresults')}</Typography>

            <Box pt="32px" pb="0px">
                <Alert severity={'info'} body={t('hereyoucanseethewinner')} />
            </Box>

            <Box mt="32px">
                <Paper padding={{ top: 24, bottom: 32 }}>
                    <CustomTable
                        headers={generateWinnerTestHeaders(t)}
                        rows={
                            isNil(pickedCampaign)
                                ? getContentLoaderResultsRows()
                                : generateResultsRows(
                                      [getIsWinner(simpleStats?.CampaignTemplatesSummary) as CampaignTemplateStats],
                                      { pickedCampaign, advancedOptions },
                                      isMobile,
                                      dispatch,
                                      t
                                  )
                        }
                        isEmpty={isEmpty(simpleStats)}
                        loading={false}
                        stickyHeader={true}
                        rowPadding={48}
                        rowHeight={112}
                        hideCheckboxes={true}
                        columnsWidth={columnsWidth}
                        emptyStateProps={{ text: t('emptystate') }}
                        disableRowClick={true}
                        disableFirstRowBorderTop={true}
                    />
                </Paper>
            </Box>
        </Box>
    );
};

export const WinnerResultsMobile: FC<WinnerResultsTableType> = (props: WinnerResultsTableType) => {
    const { pickedCampaign, advancedOptions } = props;
    const { simpleStats, campaign } = pickedCampaign;
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const isMobile = useMobile();
    const getPreviewCampaign = (campaign: Campaign, isWithTestResult: boolean) => {
        dispatch(PreviewCampaign(campaign, isWithTestResult));
    };

    const onPreviewClick = (row) => {
        const fullTemplateObject = find(campaign?.CampaignTemplates, (template) => template.Name === row.tableTitle);
        getPreviewCampaign({ ...campaign, CampaignTemplates: [fullTemplateObject] }, true);
    };

    return (
        <Box mt="64px">
            <Typography variant="h1">{t('winnerresults')}</Typography>

            <Box pt="32px" pb="0px">
                <Alert severity={'info'} body={t('hereyoucanseethewinner')} />
            </Box>

            <Box mt="32px">
                <Box pt="16px" pb="32px">
                    <MobileTable
                        headers={GenerateWinnerTestHeadersMobile()}
                        rows={
                            isNil(pickedCampaign)
                                ? getContentLoaderResultsRowsMobile()
                                : generateResultsRowsMobile(
                                      [getIsWinner(simpleStats?.CampaignTemplatesSummary) as CampaignTemplateStats],
                                      { pickedCampaign, advancedOptions },
                                      isMobile,
                                      t
                                  )
                        }
                        buttonText={t('viewtemplate')}
                        onClickButton={(ev, row) => {
                            onPreviewClick(row);
                        }}
                        disableSimplePopover
                    />
                </Box>
            </Box>
        </Box>
    );
};

const columnsWidth = {
    preview: '8%',
    template: '25%',
    delivered: '8%',
    opened: '8%',
    clicked: '8%',
};

const getContentLoaderResultsRows = (): CustomTableRowsProps[] => {
    const contentLoader = <ContentLoader mode={'row'} />;
    return [
        {
            Preview: contentLoader,
            Template: contentLoader,
            Version: contentLoader,
            Recipients: contentLoader,
            Delivered: contentLoader,
            Opened: contentLoader,
            OpenedPercentage: contentLoader,
            Clicked: contentLoader,
            ClickedPercentage: contentLoader,
            Bounced: contentLoader,
            Unsubscibed: contentLoader,
            Complained: contentLoader,
            SenderInfo: contentLoader,
            IsWinner: contentLoader,
        },
    ];
};
const getContentLoaderResultsRowsMobile = (): CustomTableRowsProps[] => {
    const contentLoader = <ContentLoader mode={'row'} />;
    return [
        {
            From: contentLoader,
            Version: contentLoader,
            Recipients: contentLoader,
            Delivered: contentLoader,
            Opened: contentLoader,
            OpenedPercentage: contentLoader,
            Clicked: contentLoader,
            ClickedPercentage: contentLoader,
            Bounced: contentLoader,
            Unsubscibed: contentLoader,
            Complained: contentLoader,
            SenderInfo: contentLoader,
            IsWinner: contentLoader,
        },
    ];
};
const generateWinnerTestHeaders = (t: TFunction): CustomTableHeadersProps[] => [
    {
        id: 'preview',
        label: '',
    },
    {
        id: 'template',
        label: t('template'),
    },
    {
        id: 'version',
        label: t('version'),
        headerAlign: 'right',
    },
    {
        id: 'recipients',
        label: t('recipents'),
        headerAlign: 'right',
    },
    {
        id: 'delivered',
        label: t('delivered'),
        headerAlign: 'right',
    },
    {
        id: 'opened',
        label: t('opened'),
        headerAlign: 'right',
    },
    {
        id: 'clicked',
        label: t('clicked'),
        headerAlign: 'right',
    },
    {
        id: 'bounced',
        label: t('bounced'),
        headerAlign: 'right',
    },
    {
        id: 'unsubscibed',
        label: t('unsubscribed'),
        headerAlign: 'right',
    },
    {
        id: 'complained',
        label: t('complained'),
        headerAlign: 'right',
    },
];

const GenerateWinnerTestHeadersMobile = (): string[] => {
    const { t } = useTranslation();
    return [
        t('from'),
        t('version'),
        t('recipents'),
        t('delivered'),
        t('opened'),
        t('clicked'),
        t('bounced'),
        t('unsubscribed'),
        t('complained'),
    ];
};

export const TestResults = (props: { simpleStats: CampaignSimpleStats }): JSX.Element => {
    const { t } = useTranslation();
    const { simpleStats } = props;

    const isNewSplit = isNewAbSplit(simpleStats?.CampaignTemplatesSummary);

    return (
        <>
            <Typography variant="h1">
                {isNewSplit ? t('testresults') : t('results').charAt(0).toUpperCase() + t('results').slice(1)}
            </Typography>

            {TestResultsBanners(simpleStats)}
        </>
    );
};

const TestResultsBanners = (simpleStats: CampaignSimpleStats): JSX.Element => {
    const isNewSplit = isNewAbSplit(simpleStats?.CampaignTemplatesSummary);
    const isWinnerTemplateSimpleStats = getWinnerTemplate(simpleStats?.CampaignTemplatesSummary);
    const { t } = useTranslation();

    return (
        <Box pt="32px" pb="16px">
            {isNewSplit && !isNil(isWinnerTemplateSimpleStats) && (
                <Alert severity={'info'} body={t('thisdatashowstheresultsoftheabtest')} />
            )}
            {isNewSplit && isNil(isWinnerTemplateSimpleStats) && <TestResultsBanner />}
            <DisabledTrackingAlert />
        </Box>
    );
};

const TestResultsBanner = (): JSX.Element => {
    const { t } = useTranslation();
    const { IsSub } = useSelector((state: ApplicationState) => state.account.GeneralInfo);

    return (
        <Box pt="4px" pb="32px">
            <Banner
                title={t('werecollectingyourabtestresults')}
                body={
                    <div>
                        {t('oncethedatawillbecollected')}{' '}
                        {!IsSub ||
                            (!isReseller() && (
                                <>
                                    {t('moredetails')} <Link href={links.howToRunAnAXsplitTest}>{t('here')}</Link>.
                                </>
                            ))}
                    </div>
                }
                imageToDisplay={<CollectSvg />}
                icon={<SimpleLoader />}
                isInfo={false}
            />
        </Box>
    );
};

export const TestResultsTable = (props: WinnerResultsTableType): JSX.Element => {
    const { t } = useTranslation();
    const { pickedCampaign, advancedOptions } = props;
    const { simpleStats } = pickedCampaign;

    const isMobile = useMobile();
    const dispatch = useDispatch();

    const filteredTemplatesSummary = isNewAbSplit(simpleStats?.CampaignTemplatesSummary)
        ? filter(simpleStats?.CampaignTemplatesSummary, (template) => !template.IsWinner)
        : simpleStats?.CampaignTemplatesSummary;

    return (
        <Box pt="16px" pb="32px">
            <Paper padding={{ top: 24, bottom: 32 }}>
                <CustomTable
                    headers={generateWinnerTestHeaders(t)}
                    rows={
                        isNil(pickedCampaign)
                            ? getContentLoaderResultsRows()
                            : generateResultsRows(
                                  filteredTemplatesSummary,
                                  { pickedCampaign, advancedOptions },
                                  isMobile,
                                  dispatch,
                                  t
                              )
                    }
                    isEmpty={isEmpty(simpleStats)}
                    loading={false}
                    stickyHeader={true}
                    rowPadding={48}
                    rowHeight={112}
                    hideCheckboxes={true}
                    columnsWidth={columnsWidth}
                    emptyStateProps={{ text: t('emptystate') }}
                    disableRowClick={true}
                    disableFirstRowBorderTop={true}
                />
            </Paper>
        </Box>
    );
};

export const TestResultsTableMobile = (props: WinnerResultsTableType): JSX.Element => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const { pickedCampaign, advancedOptions } = props;
    const { simpleStats, campaign } = pickedCampaign;
    const getPreviewCampaign = (campaign: Campaign, isWithTestResult: boolean) => {
        dispatch(PreviewCampaign(campaign, isWithTestResult));
    };
    const isMobile = useMobile();
    const filteredTemplatesSummary = isNewAbSplit(simpleStats?.CampaignTemplatesSummary)
        ? filter(simpleStats?.CampaignTemplatesSummary, (template) => !template.IsWinner)
        : simpleStats?.CampaignTemplatesSummary;

    const onPreviewClick = (row) => {
        const fullTemplateObject = find(campaign?.CampaignTemplates, (template) => template.Name === row.tableTitle);
        getPreviewCampaign({ ...campaign, CampaignTemplates: [fullTemplateObject] }, true);
    };

    return (
        <Box pt="16px">
            <MobileTable
                headers={GenerateWinnerTestHeadersMobile()}
                rows={
                    isNil(pickedCampaign)
                        ? getContentLoaderResultsRowsMobile()
                        : generateResultsRowsMobile(
                              filteredTemplatesSummary,
                              { pickedCampaign, advancedOptions },
                              isMobile,
                              t
                          )
                }
                buttonText={t('viewtemplate')}
                onClickButton={(ev, row) => {
                    onPreviewClick(row);
                }}
                disableSimplePopover
            />
        </Box>
    );
};

const RenderResultTablePreview = (getPreviewCampaign, fullTemplateObject, campaign): JSX.Element => (
    <div onClick={(ev): void => ev.stopPropagation()}>
        <TemplatePreview
            split={false}
            screenshot={
                fullTemplateObject?.MediumScreenshot
                    ? `${getApiPath()}/userfile${fullTemplateObject?.MediumScreenshot}`
                    : ''
            }
            onClick={(): void => {
                getPreviewCampaign({ ...campaign, CampaignTemplates: [fullTemplateObject] }, true);
            }}
        />
    </div>
);

const RenderResultTableTemplate = (t, single, senderInfo, subject?: string): JSX.Element => (
    <div>
        <TableTitleWrapper>
            <Typography variant="caption">
                {truncate(subject ?? single.Name, { length: 70 })}
                {single.IsWinnerTemplate && <StatusBadge variant="Completed" value={t('winnerCaptalize') as string} />}
            </Typography>
        </TableTitleWrapper>
        <TableSenderInfo>{senderInfo}</TableSenderInfo>
    </div>
);

const ResultTableDelivered = (single, isMobile): JSX.Element => (
    <TableItemBox>
        <Typography variant="caption">{formatNumber(single.Stats.Delivered)}</Typography>
        <TableItem>
            {isMobile && <SmallerNumber>{`${single.Stats.DeliveredPercentage}%`}</SmallerNumber>}
            {!isMobile && (
                <Typography variant="body1" bodyVariant="help">
                    {`${single.Stats.DeliveredPercentage}%`}
                </Typography>
            )}
        </TableItem>
    </TableItemBox>
);

const ResultTableOpened = (single, isOpensTracked, isLinkTracked, isMobile, t): JSX.Element => (
    <TableItemBox>
        <Typography variant="caption">
            {isOpensTracked && isLinkTracked ? formatNumber(single.Stats.Opened) : t('na')}
        </Typography>
        <TableItem>
            {isMobile && (
                <SmallerNumber>
                    {isOpensTracked && isLinkTracked ? `${single.Stats.OpenedPercentage}%` : t('trackingdisabled')}
                </SmallerNumber>
            )}
            {!isMobile && (
                <Typography variant="body1" bodyVariant="help">
                    {isOpensTracked && isLinkTracked ? `${single.Stats.OpenedPercentage}%` : t('trackingdisabled')}
                </Typography>
            )}
        </TableItem>
    </TableItemBox>
);

const ResultTableClicked = (single, isClicksTracked, isLinkTracked, isMobile, t): JSX.Element => (
    <TableItemBox>
        <Typography variant="caption">
            {isClicksTracked && isLinkTracked ? formatNumber(single.Stats.Clicked) : t('na')}
        </Typography>
        <TableItem>
            {isMobile && (
                <SmallerNumber>
                    {isClicksTracked && isLinkTracked ? `${single.Stats.ClickedPercentage}%` : t('trackingdisabled')}
                </SmallerNumber>
            )}
            {!isMobile && (
                <Typography variant="body1" bodyVariant="help">
                    {isClicksTracked && isLinkTracked ? `${single.Stats.ClickedPercentage}%` : t('trackingdisabled')}
                </Typography>
            )}
        </TableItem>
    </TableItemBox>
);

const ResultTableBounced = (single, isMobile): JSX.Element => (
    <TableItemBox>
        <Typography variant="body1" align="right">
            {formatNumber(single.Stats.Bounced)}
        </Typography>
        <TableItem>
            {isMobile && <SmallerNumber>{`${single.Stats.BouncedPercentage}%`}</SmallerNumber>}
            {!isMobile && (
                <Typography variant="body1" bodyVariant="help">
                    {`${single.Stats.BouncedPercentage}%`}
                </Typography>
            )}
        </TableItem>
    </TableItemBox>
);

const ResultTableUnsubscibed = (single, isMobile): JSX.Element => (
    <TableItemBox>
        <Typography variant="body1" align="right">
            {formatNumber(single.Stats.Unsubscribed)}
        </Typography>
        <TableItem>
            {isMobile && <SmallerNumber>{`${single.Stats.UnsubscribedPercentage}%`}</SmallerNumber>}
            {!isMobile && (
                <Typography variant="body1" bodyVariant="help">
                    {`${single.Stats.UnsubscribedPercentage}%`}
                </Typography>
            )}
        </TableItem>
    </TableItemBox>
);

const ResultTableComplained = (single, isMobile): JSX.Element => (
    <TableItemBox>
        <Typography variant="body1" align="right">
            {formatNumber(single.Stats.Complaints)}
        </Typography>
        <TableItem>
            {isMobile && <SmallerNumber>{`${single.Stats.ComplaintsPercentage}%`}</SmallerNumber>}
            {!isMobile && (
                <Typography variant="body1" bodyVariant="help">
                    {`${single.Stats.ComplaintsPercentage}%`}
                </Typography>
            )}
        </TableItem>
    </TableItemBox>
);

const generateResultsRows = (
    statsSummary: CampaignTemplateStats[],
    props: {
        pickedCampaign: PickedCampaignInterface;
        advancedOptions: AdvancedOptions;
    },
    isMobile: boolean,
    dispatch: Dispatch<any>,
    t: TFunction
): CustomTableRowsProps[] => {
    const getPreviewCampaign = (params) => {
        dispatch(PreviewCampaign(params));
    };

    const { pickedCampaign, advancedOptions } = props;
    const { simpleStats, templateLabels, campaign } = pickedCampaign;
    const isClicksTracked = isClickTrackingEnabled(campaign, advancedOptions);
    const isOpensTracked = isOpenTrackingEnabled(campaign, advancedOptions);
    const isLinkTracked = isLinkClickTrackingEnabled(advancedOptions);
    const winnerTemplate = getWinnerTemplate(simpleStats?.CampaignTemplatesSummary);

    return map(statsSummary, (single, index) => {
        const fullTemplateObject = find(campaign?.CampaignTemplates, (template) => template.Name === single.Name);
        const senderInfo = `${fullTemplateObject.Sender.FromName} (${fullTemplateObject.Sender.From})`;

        return {
            Preview: RenderResultTablePreview(getPreviewCampaign, fullTemplateObject, campaign),
            Template: RenderResultTableTemplate(t, single, senderInfo, fullTemplateObject.Body?.Subject),
            Version: (
                <Typography variant="body1" align="right">
                    {single.IsWinner ? templateLabels[winnerTemplate.Name] : templateLabels[single.Name]}
                </Typography>
            ),
            Recipients: (
                <Typography variant="body1" align="right">
                    {formatNumber(single.Stats.Recipients)}
                </Typography>
            ),
            Delivered: ResultTableDelivered(single, isMobile),
            Opened: ResultTableOpened(single, isOpensTracked, isLinkTracked, isMobile, t),
            Clicked: ResultTableClicked(single, isClicksTracked, isLinkTracked, isMobile, t),
            Bounced: ResultTableBounced(single, isMobile),
            Unsubscibed: ResultTableUnsubscibed(single, isMobile),
            Complained: ResultTableComplained(single, isMobile),
            rowID: `${single.Name}-${index}`,
        };
    });
};

const SenderInfo = (sender: Sender): JSX.Element => (
    <TableItemBox>
        <Typography variant="caption">
            <SenderName>{sender.FromName}</SenderName>
        </Typography>

        <TableItem>
            <Typography variant="body1" bodyVariant="help">
                <SenderEmail>{`(${sender.From})`}</SenderEmail>
            </Typography>
        </TableItem>
    </TableItemBox>
);

const WinnerTitle = (name): JSX.Element => {
    const { t } = useTranslation();
    const winner = t('winnerCaptalize');
    return (
        <Typography variant="body1" align="left">
            {name}
            <BadgeWrapper>
                <StatusBadge status="Completed" value={winner} />
            </BadgeWrapper>
        </Typography>
    );
};

const generateResultsRowsMobile = (
    statsSummary: CampaignTemplateStats[],
    props: {
        pickedCampaign: PickedCampaignInterface;
        advancedOptions: AdvancedOptions;
    },
    isMobile: boolean,
    t: TFunction
): CustomTableRowsProps[] => {
    const { pickedCampaign, advancedOptions } = props;
    const { simpleStats, templateLabels, campaign } = pickedCampaign;
    const isClicksTracked = isClickTrackingEnabled(campaign, advancedOptions);
    const isOpensTracked = isOpenTrackingEnabled(campaign, advancedOptions);
    const isLinkTracked = isLinkClickTrackingEnabled(advancedOptions);
    const winnerTemplate = getWinnerTemplate(simpleStats?.CampaignTemplatesSummary);

    return map(statsSummary, (single) => {
        const fullTemplateObject = find(campaign?.CampaignTemplates, (template) => template.Name === single.Name);

        return {
            From: SenderInfo(fullTemplateObject.Sender),
            Version: (
                <Typography variant="body1" align="right">
                    {single.IsWinner ? templateLabels[winnerTemplate.Name] : templateLabels[single.Name]}
                </Typography>
            ),
            Recipients: (
                <Typography variant="body1" align="right">
                    {formatNumber(single.Stats.Recipients)}
                </Typography>
            ),
            Delivered: ResultTableDelivered(single, isMobile),
            Opened: ResultTableOpened(single, isOpensTracked, isLinkTracked, isMobile, t),
            Clicked: ResultTableClicked(single, isClicksTracked, isLinkTracked, isMobile, t),
            Bounced: ResultTableBounced(single, isMobile),
            Unsubscibed: ResultTableUnsubscibed(single, isMobile),
            Complained: ResultTableComplained(single, isMobile),
            tableTitle: single.IsWinnerTemplate ? WinnerTitle(single.Name) : single.Name,
        };
    });
};
